import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle, XCircle, Clock, MapPin } from "@phosphor-icons/react";

/**
 * Public Owner Approval landing page.
 * Route: /owner-approval/:token?decision=approve|reject (autofill from email deep-link)
 * No login required — token is the auth.
 */
export default function OwnerApprovalPublic() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preselect = (sp.get("decision") || "").toLowerCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [decision, setDecision] = useState(preselect === "reject" ? "reject" : (preselect === "approve" ? "approve" : ""));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/owner-approval/${token}`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Invalid or expired link");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const decide = async (d) => {
    setBusy(true);
    try {
      const r = await api.post(`/public/owner-approval/${token}/decide`, {
        decision: d, note: note.trim(),
      });
      if (r.data.already_decided) {
        toast.info(`Already ${r.data.decision}ed on ${(r.data.decided_at || "").slice(0, 10)}`);
      } else {
        toast.success(`Decision recorded: ${r.data.decision.toUpperCase()}`);
      }
      // Reload state
      const nr = await api.get(`/public/owner-approval/${token}`);
      setData(nr.data);
      setDecision("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to record decision"));
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="text-slate-500 font-medium">Loading…</div>
    </div>;
  }
  if (err || !data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <XCircle weight="fill" size={48} className="mx-auto text-red-500"/>
        <div className="mt-3 font-display font-black text-xl">{err || "Not found"}</div>
        <div className="mt-2 text-sm text-slate-500">If you received this link in error, please contact your property manager.</div>
      </div>
    </div>;
  }

  const dollars = ((data.estimate_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const decidedAlready = data.status === "approved" || data.status === "rejected";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="owner-approval-public">
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-xl border-2 border-slate-900 overflow-hidden">
        <div className="p-5 bg-slate-900 text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck weight="fill" size={20} className="text-emerald-400"/>
            <div className="font-display font-black uppercase text-lg tracking-tight leading-none">
              Owner Approval Request
            </div>
          </div>
          <div className="mt-2 text-[10px] font-bold tracking-widest uppercase opacity-70">
            Requested {(data.requested_at || "").slice(0, 10)}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Property + WO context */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Property</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <MapPin weight="bold" size={14} className="text-slate-600"/>
              <span className="font-bold text-slate-900">{data.address}</span>
              {data.unit && <span className="text-slate-500 text-sm">· Unit {data.unit}</span>}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Work Order</div>
            <div className="font-bold text-slate-900">{data.title}</div>
            {data.wo && <div className="text-xs text-slate-500 font-mono">WO#{data.wo}</div>}
          </div>

          <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">Estimated Cost</div>
            <div className="text-4xl font-display font-black text-slate-900 mt-1">${dollars}</div>
          </div>

          {data.note && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Note from PM</div>
              <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{data.note}</p>
            </div>
          )}

          {/* Decision UI */}
          {decidedAlready ? (
            <div className={`p-4 rounded border-2 ${data.decision === "approve"
              ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}>
              <div className="flex items-center gap-2">
                {data.decision === "approve" ? (
                  <CheckCircle weight="fill" size={24} className="text-emerald-600"/>
                ) : (
                  <XCircle weight="fill" size={24} className="text-red-600"/>
                )}
                <div>
                  <div className="font-display font-black uppercase tracking-tight text-lg">
                    {data.decision === "approve" ? "Approved" : "Rejected"}
                  </div>
                  <div className="text-xs text-slate-600">Decided {(data.decided_at || "").slice(0, 10)}</div>
                </div>
              </div>
              {data.decision_note && (
                <p className="mt-2 text-sm text-slate-700 italic">"{data.decision_note}"</p>
              )}
              <p className="mt-3 text-[11px] text-slate-500">
                This decision is final and on record. You can close this window.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Optional Note (attached to decision)
                </label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="Any conditions, questions, or context…"
                  data-testid="owner-decision-note"
                  className="mt-1 w-full p-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-slate-500 focus:outline-none"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => decide("approve")}
                  disabled={busy}
                  data-testid="owner-decide-approve"
                  className={`h-14 rounded-lg font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 shadow-lg ${decision === "approve" ? "bg-emerald-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  <CheckCircle weight="fill" size={18}/> Approve
                </button>
                <button onClick={() => decide("reject")}
                  disabled={busy}
                  data-testid="owner-decide-reject"
                  className={`h-14 rounded-lg font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 shadow-lg ${decision === "reject" ? "bg-red-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                  <XCircle weight="fill" size={18}/> Reject
                </button>
              </div>
              <p className="text-[11px] text-slate-500 text-center">
                Your decision is stamped on the work order and cannot be changed. Legal record kept.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
